'use client'

import ParseHtml from '@/lib/utils/parse-html'

import React from 'react'

import type { AcfFields, HeadingFieldProps } from '@/types/types'

import RenderComponents from '@/components/flex-content/RenderComponents'

type AccordionProps = {
  items: AccordionItem[]
}

type AccordionItem = {
  title: HeadingFieldProps
  content?: string
  children?: string | JSX.Element | React.ReactNode
  accordion_content?: AcfFields[]
  open?: boolean
}

export default function Accordion({ items }: AccordionProps) {
  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className="component component--accordion">
      <div className="container @container">
        <div className="flex justify-center border-gradient gradient-angle-[to_bottom] @[0px]/single-simple:!border-0 @m-up:border-l @m-up:border-r">
          <div className="w-full @container/nested-flex @m-up:max-w-[var(--container-s-max-w)]">
            {items.map((item, index) => {
              if (!item.content && !item.children && !item.accordion_content) {
                return null
              }

              return (
                <AccordionItem key={index} {...item}>
                  {item.content ? (
                    <div className="style-lists">
                      <ParseHtml html={item.content} />
                    </div>
                  ) : null}

                  {item.accordion_content ? (
                    <RenderComponents components={item.accordion_content} />
                  ) : null}
                </AccordionItem>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export function AccordionItem({ title, children, open = false }: AccordionItem) {
  const { tag: titleTag = 'span', text: titleText } = title

  const detailsClass =
    'appearance-none [&_svg]:open:rotate-180 [&>summary]:open:bg-theme-accent border-t [&:not(:open)]:border-gradient open:border-theme-accent last:border-b'
  const summaryClass = '[&::-webkit-details-marker]:hidden cursor-pointer'

  return (
    <details open={open} className={detailsClass}>
      <summary
        className={`list-none appearance-none py-s pl-m pr-s ms:px-s ${summaryClass} flex items-center justify-between gap-m`}>
        {titleText ? React.createElement(titleTag, { className: `h6 mb-0` }, titleText) : null}

        <svg aria-hidden="true" className="aspect-square w-[24px] shrink-0 text-fg-title">
          <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-chevron-down`}></use>
        </svg>
      </summary>
      <div>
        <div className="p-m @container/main ms:px-s">{children}</div>
      </div>
    </details>
  )
}

// TODO: Schema markup?
// TODO: FAQs component
